'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslation } from '@/i18n/client';
import JDILogin from '@/app/login/utils';
import UKDrawer from '@/components/UKDrawer';
import { Button, TextField } from '@mui/material';
import StarEmpty from '@/assets/icons/star-empty.svg';
import EmptyStar from '@/assets/icons/Star.svg';
import UKToast from '@/components/UKToast';
import RefreshErrorPage from '@/components/refreshErrorPage';
import style from './index.module.scss';
import {
  GlobalFeedbackDrawerProps,
  FeedbackFormData,
  convertSubjectsToReasons,
  ExtendedFeedbackReason,
  isLoginRequired,
} from './types';
import { FeedbackSubmitVO } from './apis/feedback.interface';
import { fetchFeedbackGetList, fetchFeedbackSubmit } from './apis/feedback.api';

/**
 * 全局反馈抽屉组件
 * 基于UKDrawer实现，支持多种反馈类型（举报、问题反馈、建议等）
 */
export default function GlobalFeedbackDrawer({
  openDrawer,
  setOpenDrawer,
  title,
  sourceType,
  reasonList = [],
  contextData,
  userInfo: externalUserInfo,
  afterSubmitCb,
  submitButtonText,
  drawerHeight = 630,
  bottomBarHeight = 48,
  customReasons,
  showScore = false,
}: GlobalFeedbackDrawerProps) {
  const { t } = useTranslation();
  const pathname = usePathname();

  // 内部用户信息状态
  const [userInfo, setUserInfo] = useState(externalUserInfo || null);

  // 获取用户信息
  const getUserInfo = useCallback(async () => {
    // 在登录注册页面时不获取用户信息
    if (pathname?.includes('/login') || pathname?.includes('/register')) {
      console.log('🚫 登录注册页面，跳过获取用户信息');
      return;
    }

    // 如果已有外部传入的用户信息，直接使用
    if (externalUserInfo) {
      setUserInfo(externalUserInfo);
      return;
    }

    try {
      const { isLoggedIn, userInfo: loginUserInfo } = await JDILogin.getInstance().getUserInfo();
      console.log('👤 获取用户登录状态:', { isLoggedIn, userInfo: loginUserInfo });

      if (isLoggedIn && loginUserInfo) {
        setUserInfo({
          userPin: loginUserInfo.pin,
          email: loginUserInfo.email,
          phone: loginUserInfo.mobile,
        });
      } else {
        setUserInfo(null);
      }
    } catch (error) {
      console.error('❌ 获取用户信息失败:', error);
      setUserInfo(null);
    }
  }, [pathname, externalUserInfo]);

  // 表单状态
  const [formData, setFormData] = useState<FeedbackFormData>({
    reasonId: '',
    content: '',
    additionalData: contextData,
    sourceType: sourceType,
    starScore: 0,
  });

  // 页面状态
  const [reasons, setReasons] = useState<ExtendedFeedbackReason[]>(reasonList);
  const [isError, setIsError] = useState(false);
  const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);

  // 获取当前选择的原因
  const getCurrentReason = () => {
    return reasons.find((reason) => reason.id === formData.reasonId) as
      | ExtendedFeedbackReason
      | undefined;
  };

  // 判断是否需要显示输入框（Other类型）
  const shouldShowInputBox = () => {
    const currentReason = getCurrentReason();
    if (!currentReason) return false;

    // 直接使用API返回的showInputBox字段
    return currentReason.showInputBox === true;
  };

  // 判断是否有任何原因需要输入框
  const hasAnyInputBoxReason = () => {
    return reasons.some((reason) => reason.showInputBox === true);
  };

  // 获取反馈原因列表
  const getReasonList = useCallback(async () => {
    if (customReasons && customReasons.length > 0) {
      setReasons(customReasons);
      return;
    }
    setIsError(false);

    try {
      console.log('🚀 调用反馈原因接口，参数:', { sourceType });
      const result = await fetchFeedbackGetList({ sourceType });
      console.log('📋 反馈原因接口返回数据:', result);

      if (result.success && result.data && result.data.subjectInfoDTOList) {
        console.log('✅ 接口调用成功，原始主题列表:', result.data.subjectInfoDTOList);

        const convertedReasons = convertSubjectsToReasons(result.data.subjectInfoDTOList);
        console.log('🔄 转换后的原因列表:', convertedReasons);

        setReasons(convertedReasons);
      } else {
        console.warn('❌ 获取反馈主题失败:', result.message);
        console.log('❌ 完整错误响应:', result);
        setIsError(true);
      }
    } catch (error) {
      console.error('🔥 获取反馈主题异常:', error);
      setIsError(true);
    }
  }, [customReasons, sourceType]);

  // 检查登录状态并处理跳转
  const checkLoginAndProceed = useCallback(async () => {
    // 如果是 Login_Feedback 类型，不需要检查登录状态
    if (!isLoginRequired(sourceType)) {
      return true;
    }

    // 检查用户登录状态
    try {
      const { isLoggedIn } = await JDILogin.getInstance().getUserInfo();

      if (!isLoggedIn) {
        // 关闭当前抽屉
        setOpenDrawer(false);
        // 跳转到登录页面
        await JDILogin.getInstance().goToLoginPage();
        return false;
      }

      return true;
    } catch (error) {
      // 出错时也跳转到登录页面
      setOpenDrawer(false);
      await JDILogin.getInstance().goToLoginPage();
      return false;
    }
  }, [sourceType, setOpenDrawer]);

  // 组件加载时检查登录状态并获取原因列表和用户信息
  useEffect(() => {
    if (openDrawer) {
      checkLoginAndProceed().then((canProceed) => {
        if (canProceed) {
          getReasonList();
          getUserInfo();
        }
      });
    }
  }, [openDrawer, checkLoginAndProceed, getReasonList, getUserInfo]);

  // 重置状态
  useEffect(() => {
    if (openDrawer) {
      setFormData({
        reasonId: '',
        content: '',
        additionalData: contextData,
        sourceType: sourceType,
        starScore: 0,
      });
    }
  }, [openDrawer, contextData, sourceType, reasons]);

  // 处理内容输入
  const handleContentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      content: value,
    }));
  };

  // 处理星星评分
  const handleStarClick = (score: number) => {
    setFormData((prev) => ({
      ...prev,
      starScore: score,
    }));
  };

  // 星星评分组件（与 Rate 组件保持一致）
  const StarRating = ({
    value,
    onChange,
  }: {
    value: number;
    onChange: (score: number) => void;
  }) => {
    return (
      <div className={style.starRating}>
        {[1, 2, 3, 4, 5].map((star) => (
          <div key={star} className={style.star} onClick={() => onChange(star)}>
            {value >= star ? (
              <EmptyStar width={24} height={24} />
            ) : (
              <StarEmpty width={24} height={24} />
            )}
          </div>
        ))}
      </div>
    );
  };

  // 提交表单
  const handleSubmit = async () => {
    // 标记已经尝试提交
    setHasSubmitAttempt(true);
    if (!formData.reasonId) {
      UKToast.show({
        content: t('common.form.pleaseSelect', { field: t('feedback.reason') }),
        icon: 'error',
      });
      return;
    }

    // 如果存在需要输入框的原因且当前选择了该类型，必须填写详细描述
    if (hasAnyInputBoxReason() && shouldShowInputBox()) {
      if (!formData.content.trim()) {
        // 不显示Toast，让helperText显示错误
        return;
      }

      // 检查最小长度要求（至少10个字符）
      if (formData.content.trim().length < 10) {
        // 不显示Toast，让helperText显示错误
        return;
      }
    }

    try {
      console.log('📤 准备提交反馈数据:', formData);
      console.log('📋 当前原因列表:', reasons);
      console.log('👤 用户信息:', userInfo);

      // 找到对应的反馈原因
      const selectedReason = reasons.find((reason) => reason.id === formData.reasonId);

      // subject逻辑：始终传原因标题
      const subject = selectedReason?.title || 'Unknown';

      // 构建API请求参数
      const submitData: FeedbackSubmitVO = {
        subjectType: parseInt(formData.reasonId),
        subject: subject,
        feedbackDetails: formData.content,
        sourceType: formData.sourceType || 1,
        platform: 4, // M端平台
        starScore: formData.starScore,
        onlyStar: false, // 不是仅星级保存
        selection: parseInt(formData.reasonId), // 反馈选项
        userPin: userInfo?.userPin,
        email: userInfo?.email,
        phone: userInfo?.phone,
      };

      // 添加扩展参数
      if (formData.additionalData) {
        const extMap: Record<string, string> = {};

        // 商品详情页需要传递skuId
        if (formData.additionalData.skuId) {
          extMap.SKU = formData.additionalData.skuId.toString();
        }

        // 搜索页面需要传递搜索关键词
        if (formData.additionalData.searchKeyword) {
          extMap.QUERY = formData.additionalData.searchKeyword.toString();
        }

        if (Object.keys(extMap).length > 0) {
          submitData.extMap = extMap;
        }
      }

      console.log('🚀 调用提交API，参数:', submitData);
      const result = await fetchFeedbackSubmit(submitData);
      console.log('📨 提交API返回结果:', result);

      if (result.success) {
        UKToast.show({
          content: result.message || t('feedback.submitSuccess'),
          icon: 'success',
        });

        // 调用自定义回调函数
        if (afterSubmitCb) {
          afterSubmitCb(formData);
        }

        // 重置校验状态
        setHasSubmitAttempt(false);
        setOpenDrawer(false);
      } else {
        UKToast.show({
          content: result.message || t('feedback.submitFailed'),
          icon: 'error',
        });
      }
    } catch (error) {
      console.error('提交反馈失败:', error);
      UKToast.show({
        content: t('feedback.submitFailed'),
        icon: 'error',
      });
    }
  };

  // 关闭抽屉
  const handleClose = () => {
    // 重置校验状态
    setHasSubmitAttempt(false);
    setOpenDrawer(false);
  };

  // 获取标题
  const getTitle = () => {
    if (title) return title;
    return t('feedback.default.title');
  };

  return (
    <UKDrawer
      title={getTitle()}
      bottomBarHeight={bottomBarHeight}
      openDrawer={openDrawer}
      height={drawerHeight}
      setOpenDrawer={setOpenDrawer}
      handleClose={handleClose}
      className={style.feedbackDrawerWrapper}
    >
      <div className={style.feedbackContainer}>
        {isError ? (
          <RefreshErrorPage onClick={getReasonList} />
        ) : (
          <div className={style.feedbackContent}>
            {/* 星星评分 - 根据 showScore 参数控制显示 */}
            {showScore && (
              <>
                <div className={style.rateTitle}>{t('feedback.OverallSatisfaction')}</div>
                <div className={style.rateStar}>
                  <StarRating value={formData.starScore || 0} onChange={handleStarClick} />
                </div>
              </>
            )}

            {/* 原因选择 */}
            {reasons.length > 0 && (
              <div className={style.reasonSection}>
                <label className={style.reasonLabel}>{t('feedback.selectReason')}</label>
                <div className={style.reasonList}>
                  {reasons.map((reason) => (
                    <div
                      key={reason.id}
                      className={`${style.reasonItem} ${
                        formData.reasonId === reason.id ? style.selected : ''
                      }`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          reasonId: reason.id,
                          content: '', // 切换选择时清空内容
                          starScore: 0, // 重置评分
                        }))
                      }
                    >
                      <div className={style.radioContainer}>
                        <div
                          className={`${style.radioButton} ${
                            formData.reasonId === reason.id ? style.checked : ''
                          }`}
                        ></div>
                      </div>
                      <span className={style.reasonText}>{reason.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 详细描述 - 只有存在需要输入框的原因且当前选择了该类型时才显示 */}
            {hasAnyInputBoxReason() && shouldShowInputBox() && (
              <div className={style.contentSection}>
                <label className={style.contentLabel}>{t('feedback.detailedDescription')}</label>
                <div className={style.inputWrapper}>
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={8}
                    value={formData.content}
                    onChange={handleContentChange}
                    placeholder={t('feedback.contentPlaceholder')}
                    className={style.contentInput}
                    fullWidth
                    required
                    inputProps={{
                      maxLength: 100,
                    }}
                    InputProps={{
                      className: style.textareaInput,
                    }}
                    helperText={
                      hasSubmitAttempt && hasAnyInputBoxReason() && shouldShowInputBox()
                        ? !formData.content.trim()
                          ? t('common.message.textError')
                          : formData.content.trim().length < 10
                            ? t('common.message.textError')
                            : ' '
                        : ' '
                    }
                    error={
                      hasSubmitAttempt &&
                      hasAnyInputBoxReason() &&
                      shouldShowInputBox() &&
                      (!formData.content.trim() || formData.content.trim().length < 10)
                    }
                    FormHelperTextProps={{
                      error: true,
                    }}
                  />
                  <div className={style.characterCount}>{formData.content.length}/100</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* 提交按钮 - 吸底显示 */}
        {!isError && (
          <div className={style.submitSection}>
            <Button
              variant='contained'
              fullWidth
              onClick={handleSubmit}
              disabled={
                !formData.reasonId ||
                (hasAnyInputBoxReason() && shouldShowInputBox() && !formData.content.trim())
              }
              className={style.submitButton}
            >
              {t('common.btnText.Submit')}
            </Button>
          </div>
        )}
      </div>
    </UKDrawer>
  );
}
